import { AsyncBoundary } from "@components/AsyncBoundary";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { recordLabel } from "@pims/pimsActivityClient";
import { PimsOutcomeCallout } from "@pims/PimsOutcomeCallout";
import { PimsReplayPlayer } from "@pims/PimsReplayPlayer";
import {
  cn,
  IconButton,
  LedgerBody,
  LedgerCell,
  LedgerHead,
  LedgerHeadCell,
  LedgerRow,
  LedgerTable,
  Sheet,
  Skeleton,
  Swipe,
  type SwipeTone,
} from "@scribenote/brian-design-system";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import type {
  PimsAudit as PimsAuditData,
  PimsAuditScreenshot,
  PimsAuditStep,
} from "./pimsAuditClient";
import { usePimsAudit } from "./pimsAuditQueries";

/**
 * The quiet inline disclosure control used throughout the audit: an annotation
 * you can click, not a button competing with the content it reveals.
 */
const disclosure =
  "self-start text-xs font-medium text-accent-deep hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";

/**
 * The audit trail for one completed PIMS write (WEB-3237): a chronological
 * timeline interleaving what the agent did on the PIMS screen (its reasoning at
 * each step) with the screenshot that step captured, the way it worked through
 * the write. Fetched lazily (the query suspends), so it's guarded with its own
 * skeleton + retry and only mounts once its activity card is expanded.
 *
 * The trail itself is green-bar ledger paper: what happened, in order,
 * permanently. Everything around it (the header, the summary well, Brian's
 * written result) is annotation on that sheet.
 */
export const PimsAudit = ({ taskId }: { taskId: string }) => (
  <AsyncBoundary
    skeleton={<AuditSkeleton />}
    fallback={(retry) => (
      <p className="px-1 py-2 text-xs text-annotation">
        Couldn&rsquo;t load the PIMS audit.{" "}
        <button type="button" onClick={retry} className={disclosure}>
          Retry
        </button>
      </p>
    )}
  >
    <AuditContent taskId={taskId} />
  </AsyncBoundary>
);

const AuditContent = ({ taskId }: { taskId: string }) => {
  const { data: audit, refetch } = usePimsAudit(taskId);
  // Presigned screenshot/video URLs expire (~1h). A failed load past that
  // window is recovered by refetching once — the backend re-signs every URL —
  // which swaps in fresh `src`s. One shared guard covers both assets so a
  // stale page can't refetch-loop no matter which one expires first.
  const refreshed = useRef(false);
  const onAssetError = () => {
    if (refreshed.current) return;
    refreshed.current = true;
    void refetch();
  };

  // The full-size viewer is shared across the whole audit. Track the open
  // screenshot by name (not the object) and re-derive it from the latest
  // `screenshots` each render, so a presigned-URL refetch reaches the open
  // viewer too, not just the thumbnails.
  const [activeName, setActiveName] = useState<string | null>(null);
  const [screenshotsOpen, setScreenshotsOpen] = useState(false);

  if (!audit)
    return (
      <p
        className="px-1 py-2 text-xs text-annotation"
        data-testid={`pims-audit-empty-${taskId}`}
      >
        I have no audit for this write.
      </p>
    );

  const rows = buildRows(audit.steps, audit.screenshots, audit.goal);

  const hasBody =
    audit.summary != null ||
    rows.length > 0 ||
    audit.output != null ||
    audit.videoUrl != null;

  const active = activeName
    ? (audit.screenshots.find((s) => s.name === activeName) ?? null)
    : null;
  const openShot = (shot: PimsAuditScreenshot) => setActiveName(shot.name);

  // An audit whose frames all went missing would otherwise rule an empty
  // column down the sheet, so the screen column only exists when something
  // was captured.
  const showScreen = rows.some((row) => row.shot !== null);

  const screenshotRows = rows.length > 0 && (
    <LedgerTable data-testid={`pims-audit-ledger-${taskId}`}>
      <LedgerHead>
        <tr>
          <LedgerHeadCell align="right" className="w-px">
            Step
          </LedgerHeadCell>
          {showScreen && (
            <LedgerHeadCell className="w-px">Screen</LedgerHeadCell>
          )}
          <LedgerHeadCell className="w-full">What Brian did</LedgerHeadCell>
        </tr>
      </LedgerHead>
      <LedgerBody>
        {rows.map((row, i) => (
          <AuditRow
            key={row.key}
            row={row}
            seq={i + 1}
            showScreen={showScreen}
            onOpen={openShot}
            onImageError={onAssetError}
          />
        ))}
      </LedgerBody>
    </LedgerTable>
  );

  return (
    <div
      className="flex flex-col gap-3 py-1"
      data-testid={`pims-audit-body-${taskId}`}
    >
      <ReplayHeader audit={audit} />

      <PimsOutcomeCallout
        outcome={audit.failureOutcome}
        why={audit.failureWhy}
        category={audit.failureCategory}
        incidents={audit.failureIncidents}
      />

      {audit.videoUrl ? (
        <>
          <PimsReplayPlayer
            videoUrl={audit.videoUrl}
            chapters={audit.chapters}
            durationMs={audit.durationMs}
            failedChapterIndex={audit.failedChapterIndex}
            incidents={audit.failureIncidents}
            causalStepId={audit.failureCausalStepId}
            onVideoError={onAssetError}
          />
          {rows.length > 0 && (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => setScreenshotsOpen((v) => !v)}
                aria-expanded={screenshotsOpen}
                className={disclosure}
                data-testid={`pims-audit-screenshots-toggle-${taskId}`}
              >
                {screenshotsOpen ? "Hide screenshots" : "View screenshots"}
              </button>
              {screenshotsOpen && screenshotRows}
            </div>
          )}
        </>
      ) : (
        <>
          <CaptureGap status={audit.captureStatus} />
          {screenshotRows}
        </>
      )}

      <AuditSummary summary={audit.summary} />

      <AuditResult
        taskId={taskId}
        output={audit.output}
        defaultOpen={audit.status !== "done"}
      />

      {!hasBody && (
        <p className="px-1 text-xs text-annotation">
          I didn&rsquo;t record any details for this write.
        </p>
      )}

      <ScreenshotLightbox
        shots={audit.screenshots}
        active={active}
        onSelect={setActiveName}
        onClose={() => setActiveName(null)}
        onImageError={onAssetError}
      />
    </div>
  );
};

// --- Screenshot lightbox ---------------------------------------------------

/**
 * Full-size screenshot viewer. Deliberately not a Clipboard or a ledger row:
 * this is one held frame, blown up as large as the desk allows, so it stays a
 * wide Sheet.
 *
 * The frames are a sequence (their filename prefix orders them), so the viewer
 * steps through them in place (Prev/Next buttons plus the arrow keys) rather
 * than making you close and reopen for each frame. Clamped at both ends (no
 * wrap-around) so the buttons' disabled state matches what the keys do and you
 * can tell where the sequence starts and stops.
 */
const ScreenshotLightbox = ({
  shots,
  active,
  onSelect,
  onClose,
  onImageError,
}: {
  shots: PimsAuditScreenshot[];
  active: PimsAuditScreenshot | null;
  onSelect: (name: string) => void;
  onClose: () => void;
  onImageError: () => void;
}) => {
  const index = active ? shots.findIndex((s) => s.name === active.name) : -1;
  const hasPrev = index > 0;
  const hasNext = index >= 0 && index < shots.length - 1;

  const step = (delta: number) => {
    const next = shots[index + delta];
    if (next) onSelect(next.name);
  };

  // Document-level so the keys work wherever focus landed inside the sheet
  // (the image, a nav button, the close X). Escape stays the Sheet's business.
  useEffect(() => {
    if (index < 0) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const next = shots[index + (e.key === "ArrowRight" ? 1 : -1)];
      if (!next) return;
      e.preventDefault();
      onSelect(next.name);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [index, shots, onSelect]);

  return (
    <Sheet
      open={active !== null}
      onClose={onClose}
      size="lg"
      // A screenshot of a PIMS screen is unreadable at a reading-width sheet,
      // so this one is allowed to take the desk: `cn` merges, so this displaces
      // the `lg` size's own max-width.
      className="max-w-[80vw]"
      // Dropped while closed: a native <dialog> stays mounted, so an
      // unconditional testid would report an open viewer that isn't there.
      data-testid={active ? "pims-audit-lightbox" : undefined}
      title={
        <span className="flex flex-wrap items-baseline gap-x-2">
          <span className="font-mono text-base">
            {active?.name ?? "Screenshot"}
          </span>
          {shots.length > 1 && index >= 0 && (
            <span className="text-annotation-caps text-annotation">
              {index + 1} of {shots.length}
            </span>
          )}
        </span>
      }
    >
      {active && (
        <div className="flex flex-col gap-3">
          <img
            src={active.url}
            alt={active.name}
            onError={onImageError}
            className="max-h-[70vh] w-full rounded-paper bg-recess object-contain"
          />
          {shots.length > 1 && (
            <div className="flex items-center justify-center gap-2">
              <IconButton
                variant="outline"
                disabled={!hasPrev}
                onClick={() => step(-1)}
                data-testid="pims-audit-lightbox-prev"
                aria-label="Previous screenshot"
              >
                <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
              </IconButton>
              <IconButton
                variant="outline"
                disabled={!hasNext}
                onClick={() => step(1)}
                data-testid="pims-audit-lightbox-next"
                aria-label="Next screenshot"
              >
                <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
              </IconButton>
            </div>
          )}
        </div>
      )}
    </Sheet>
  );
};

/** A human reason no video is available for this write, keyed by the
 * backend's `capture_status`. `"unavailable"` (our sentinel for a null
 * backend status — no spine at all, i.e. a legacy pre-replay task) has no
 * entry: those tasks fall back to the plain screenshot audit with no gap
 * banner, since there was never anything to capture. */
const CAPTURE_GAP_REASONS: Record<string, string> = {
  recorder_failed: "The screen recorder failed during this write.",
  unsupported:
    "This clinic's PIMS setup doesn't support session recording yet.",
  disabled: "Session recording is turned off for this clinic.",
  invalid: "The session recording couldn't be read.",
};

const CaptureGap = ({ status }: { status: string }) => {
  if (status === "unavailable" || status === "available") return null;
  const reason =
    CAPTURE_GAP_REASONS[status] ?? "I have no video of this write.";
  return (
    <p
      className="rounded-paper bg-recess px-3 py-2 text-xs text-annotation"
      data-testid="pims-audit-capture-gap"
    >
      {reason}
    </p>
  );
};

/** "Automatic now" / "Batch" / "unknown historical mode" — the write-mode
 * label. There's no "automatic" mode in the backend (see brian's
 * `CLAUDE.local.md`); "Automatic now" is the staff-facing label for both
 * autonomous cadences (`direct` + `batch`) collapsed to their live/queued
 * distinction — `direct` reads as "happening now", `batch` as its own queued
 * label. `null` covers a read or a task that predates write-mode tracking. */
const writeModeLabel = (writeMode: string | null): string => {
  switch (writeMode) {
    case "direct":
      return "Automatic now";
    case "batch":
      return "Batch";
    case null:
      return "unknown historical mode";
    default:
      return writeMode;
  }
};

/**
 * The two autonomous cadences are the one thing on this surface that literally
 * means "Brian did this", so the mode wears the Brian marker there and nowhere
 * else. A staff-initiated write, or a task predating write-mode tracking, makes
 * no such claim and goes unmarked.
 */
const WriteMode = ({ writeMode }: { writeMode: string | null }) => {
  const label = writeModeLabel(writeMode);
  return writeMode === "direct" || writeMode === "batch" ? (
    <Swipe tone="brian">{label}</Swipe>
  ) : (
    <>{label}</>
  );
};

/**
 * Raw backend `status` → the highlighter that means it. A settled write says its
 * outcome in a word and wears the marker over it: green for the write that
 * landed, red for the one that stopped, yellow for the one waiting on a person.
 *
 * Anything absent from the map goes unmarked: `cancelled` never claimed to do
 * anything, and an unrecognised future status is not something to make a claim
 * about. The non-terminal entries are only reachable on a lenient or legacy
 * payload, since the evidence endpoint is for settled writes.
 */
const STATUS_TONE: Record<string, SwipeTone> = {
  done: "working",
  failed: "stopped",
  timeout: "stopped",
  needs_clarification: "flag",
  running: "working",
  dispatched: "working",
};

const ReplayHeader = ({ audit }: { audit: PimsAuditData }) => {
  const statusLabel =
    audit.status.charAt(0).toUpperCase() + audit.status.slice(1);
  const tone = STATUS_TONE[audit.status];
  const record = audit.record ? recordLabel(audit.record) : null;
  // The goal already leads the header as the title; only surface the intent
  // line when it adds something the title doesn't (it's frequently identical).
  const showWhy = audit.intent.goal !== "" && audit.intent.goal !== audit.goal;

  return (
    <div
      className="flex flex-col gap-2 border-b border-rule pb-3"
      data-testid="pims-audit-replay-header"
    >
      <div className="flex items-start justify-between gap-2">
        <h3
          className="line-clamp-2 min-w-0 flex-1 font-display text-sm font-medium leading-snug text-ink"
          title={audit.goal}
        >
          {audit.goal}
        </h3>
        <span className="shrink-0 text-xs">
          {tone ? (
            <Swipe
              tone={tone}
              data-testid={`pims-audit-status-badge-${audit.taskId}`}
            >
              {statusLabel}
            </Swipe>
          ) : (
            <span
              className="text-annotation"
              data-testid={`pims-audit-status-badge-${audit.taskId}`}
            >
              {statusLabel}
            </span>
          )}
        </span>
      </div>
      <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-annotation">
        <span data-testid="pims-audit-mode">
          <span className="font-medium text-ink">Mode:</span>{" "}
          <WriteMode writeMode={audit.intent.writeMode} />
        </span>
        {record && (
          <span data-testid="pims-audit-record">
            <span className="font-medium text-ink">Record:</span> {record}
          </span>
        )}
      </p>
      {showWhy && (
        <p className="text-xs text-annotation" data-testid="pims-audit-intent">
          <span className="font-medium text-ink">Why Brian did this:</span>{" "}
          {audit.intent.goal}
        </p>
      )}
      {/* The status word is already marked above, so this line repeats it in
          plain ink: one highlighter per state, not two. */}
      <p className="text-xs text-annotation" data-testid="pims-audit-status">
        <span className="font-medium text-ink">Status:</span> {statusLabel}
      </p>
    </div>
  );
};

interface AuditRowData {
  key: string;
  text: string | null;
  shot: PimsAuditScreenshot | null;
}

/**
 * Screenshots are the source of truth for order: the filename prefix (`000_…`) is the
 * real gap-free capture sequence, so one row is rendered per screenshot in that order —
 * including frames no step referenced, which a message-driven order would strand.
 *
 * Messages supply only the text. Reasoning follows the action that captured a frame, so
 * each frame is labelled with the reasoning of the step *after* the one that referenced
 * it. Raw action names are dropped as noise; the opening `goal` step repeats the card
 * title, so its text is suppressed. A reasoning message whose frame is missing is
 * appended so nothing is lost.
 */
const buildRows = (
  steps: PimsAuditStep[],
  screenshots: PimsAuditScreenshot[],
  goal: string,
): AuditRowData[] => {
  // Map each frame filename → the reasoning that interprets it (the text of the
  // step following the one that referenced the frame).
  const textByShot = new Map<string, string>();
  let lastShot: string | null = null;
  for (const step of steps) {
    // Key off the filename even when the PNG is missing (no `screenshotUrl`):
    // the following reasoning still associates to it, and the append loop below
    // then surfaces that message as a text-only row rather than dropping it.
    if (step.screenshot != null) lastShot = step.screenshot;
    const text = step.text && step.text !== goal ? step.text : null;
    if (text && lastShot && !textByShot.has(lastShot)) {
      textByShot.set(lastShot, text);
      lastShot = null;
    }
  }

  let n = 0;
  const rows: AuditRowData[] = screenshots.map((shot) => ({
    key: `s${n++}`,
    text: textByShot.get(shot.name) ?? null,
    shot,
  }));
  // Messages whose referenced frame isn't in the screenshot set — keep them.
  const shotNames = new Set(screenshots.map((s) => s.name));
  for (const [name, text] of textByShot)
    if (!shotNames.has(name)) rows.push({ key: `x${n++}`, text, shot: null });
  return rows;
};

/** Collapsed message height (px) — matches the fixed screenshot height. */
const COLLAPSED_H = 160;

/**
 * One entry on the ledger: its line number in mono, the frame it captured, and
 * what Brian was doing when it did. A long piece of reasoning is cropped to the
 * height of the frame beside it and fades out at the crop, so a row stays one
 * scannable line of the sheet until someone asks for the rest. The fade is a
 * mask on the text rather than a paper-coloured gradient over it, since every
 * other row sits on a green bar and a gradient would have to guess which.
 */
const AuditRow = ({
  row,
  seq,
  showScreen,
  onOpen,
  onImageError,
}: {
  row: AuditRowData;
  seq: number;
  showScreen: boolean;
  onOpen: (shot: PimsAuditScreenshot) => void;
  onImageError: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [contentH, setContentH] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  // `scrollHeight` reports the full content height regardless of the collapsed
  // `max-height`/`overflow-hidden`, so one measure tells us whether the message
  // overflows and needs a Show more/less control + fade.
  useLayoutEffect(() => {
    if (contentRef.current) setContentH(contentRef.current.scrollHeight);
  }, [row.text]);
  const overflowing = contentH > COLLAPSED_H + 4;

  return (
    <LedgerRow data-testid={`pims-audit-row-${row.key}`}>
      <LedgerCell mono align="right" className="align-top text-annotation">
        {String(seq).padStart(2, "0")}
      </LedgerCell>
      {showScreen && (
        <LedgerCell className="align-top">
          {row.shot && (
            <button
              type="button"
              onClick={() => row.shot && onOpen(row.shot)}
              className="block overflow-hidden rounded-paper border border-rule bg-recess transition duration-150 ease-paper hover:-translate-y-px hover:shadow-lift-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent motion-reduce:transition-none"
              data-testid={`pims-audit-shot-${row.shot.name}`}
              title={row.shot.name}
            >
              <img
                src={row.shot.url}
                alt={row.shot.name}
                loading="lazy"
                onError={onImageError}
                className="h-40 w-64 object-contain"
              />
            </button>
          )}
        </LedgerCell>
      )}
      <LedgerCell className="align-top">
        {row.text && (
          <div className="min-w-0">
            <div
              ref={contentRef}
              className={cn(
                "ds-markdown overflow-hidden text-sm leading-relaxed text-ink-body [overflow-wrap:anywhere]",
                overflowing &&
                  !expanded &&
                  "[mask-image:linear-gradient(to_bottom,black_calc(100%-2.5rem),transparent)]",
              )}
              style={{ maxHeight: expanded ? undefined : COLLAPSED_H }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {row.text}
              </ReactMarkdown>
            </div>
            {overflowing && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className={cn(disclosure, "mt-1.5")}
                data-testid={`pims-audit-more-${row.key}`}
              >
                {expanded ? "Show less" : "Show more"}
              </button>
            )}
          </div>
        )}
      </LedgerCell>
    </LedgerRow>
  );
};

/** Opaque backend `summary` keys we surface; everything else is ignored. */
const AuditSummary = ({
  summary,
}: {
  summary: Record<string, unknown> | null;
}) => {
  if (!summary) return null;

  const changes = asChanges(summary.changes);
  const verification = asVerification(summary.verification);
  const meta = [
    typeof summary.steps === "number" ? `${summary.steps} steps` : null,
    typeof summary.latency_s === "number"
      ? `${Math.round(summary.latency_s)}s`
      : null,
  ].filter((v): v is string => v !== null);

  if (changes.length === 0 && !verification && meta.length === 0) return null;

  return (
    <div className="flex flex-col gap-1.5 rounded-paper bg-recess px-3 py-2">
      {verification && (
        <p className="text-xs text-annotation">
          {/* The verdict is a secondary annotation inside a well, not the
              write's outcome, so it takes the status ink rather than a second
              highlighter under the one already worn by the status above. */}
          <span
            className={
              verification.verdict === "pass"
                ? "font-medium text-ink-working"
                : "font-medium text-ink-stopped"
            }
          >
            {verification.verdict === "pass" ? "Verified" : "Not verified"}
          </span>
          {verification.reason ? `: ${verification.reason}` : ""}
        </p>
      )}
      {changes.length > 0 && (
        <ul className="text-xs text-annotation">
          {changes.map((c, i) => (
            <li key={i}>
              Set <span className="text-ink">{c.field}</span> to{" "}
              <span className="text-ink">{c.to}</span>
            </li>
          ))}
        </ul>
      )}
      {meta.length > 0 && (
        <p className="font-mono text-xs text-annotation">{meta.join(" · ")}</p>
      )}
    </div>
  );
};

/**
 * Brian's verbose written result (`output`) — a full markdown report. It's the least
 * glanceable thing in the audit, so it lives last and collapsed: a `done` write's replay
 * and summary already tell the story, and the report is there when someone wants the
 * verbatim answer. Non-`done` writes open it by default, since there the output *is* the
 * failure reason.
 *
 * Rendered full pane width — wide tables scroll horizontally in their own track — rather
 * than boxed into a cramped inner scroller.
 */
const AuditResult = ({
  taskId,
  output,
  defaultOpen,
}: {
  taskId: string;
  output: string | null;
  defaultOpen: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!output) return null;

  return (
    <div className="flex flex-col gap-2" data-testid="pims-audit-result">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={disclosure}
        data-testid={`pims-audit-result-toggle-${taskId}`}
      >
        {open ? "Hide Brian’s result" : "Show Brian’s result"}
      </button>
      {open && (
        <div className="ds-markdown overflow-x-auto rounded-paper bg-recess px-3 py-2.5 text-sm leading-relaxed text-ink-body [overflow-wrap:anywhere]">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{output}</ReactMarkdown>
        </div>
      )}
    </div>
  );
};

interface Change {
  field: string;
  to: string;
}

const asChanges = (value: unknown): Change[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((c) => {
    if (c && typeof c === "object" && "field" in c && "to" in c) {
      const { field, to } = c as Record<string, unknown>;
      return [{ field: String(field), to: String(to) }];
    }
    return [];
  });
};

const asVerification = (
  value: unknown,
): { verdict: string; reason: string | null } | null => {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  if (typeof v.verdict !== "string") return null;
  return {
    verdict: v.verdict,
    reason: typeof v.reason === "string" ? v.reason : null,
  };
};

/** The ledger sheet before anything is written on it: the rules and the green
 * bars are already there, the entries aren't. The band is LedgerRow's own
 * expression, so the sheet doesn't re-stripe itself when the entries land. */
const AuditSkeleton = () => (
  <div className="py-1">
    <div className="overflow-hidden rounded-paper border border-rule bg-card">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="flex items-start gap-3 border-b border-rule/50 p-3 even:bg-[color-mix(in_srgb,var(--color-wash-working)_16%,transparent)] last:border-b-0"
        >
          <Skeleton className="h-40 w-64 shrink-0" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-11/12" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        </div>
      ))}
    </div>
  </div>
);
